import { ReactNode, CSSProperties } from "react";
import cx from "classnames";

import { type IconComponent } from "types/Icon";
import Tooltip from "ds/components/Tooltip";
import BaseAction, { type BaseActionProps } from "ds/components/BaseAction";
import { TooltipModalReferenceProps } from "ds/components/TooltipModal/types";
import { MainNavigationTransition } from "components/MainNavigation/hooks/useFadeTransition";

import MainNavigationBaseActionContent from "./Content";
import styles from "./styles.module.css";

type MainNavigationBaseActionLinkProps = BaseActionProps & {
  title: string;
  icon?: IconComponent;
  isSidebarExpanded?: boolean;
  isActive?: boolean;
  isFocused?: boolean;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  tooltipModalRef?: TooltipModalReferenceProps["ref"];
} & MainNavigationTransition;

const MainNavigationBaseActionLink = ({
  title,
  icon,
  isSidebarExpanded,
  isActive,
  isFocused,
  transitionClasses,
  children,
  className,
  style,
  tooltipModalRef,
  ...restProps
}: MainNavigationBaseActionLinkProps) => {
  const combinedClassName = cx(
    styles.baseAction,
    styles.baseActionLink,
    {
      [styles.baseActionActive]: isActive,
      [styles.baseActionFocused]: isFocused,
    },
    className
  );

  const isExternalLink = "href" in restProps && !!restProps.href;

  return (
    <Tooltip
      disableAriaLabel
      active={!isSidebarExpanded}
      placement="right"
      on={({ ref: tooltipRef, ...tooltipProps }) => {
        return (
          <BaseAction
            aria-label={title}
            {...tooltipProps}
            {...restProps}
            target={isExternalLink ? "_blank" : undefined}
            ref={(node) => {
              tooltipRef(node);
              tooltipModalRef?.(node);
            }}
            className={combinedClassName}
            justify="between"
            align="center"
            fullWidth
            style={style}
          >
            <MainNavigationBaseActionContent
              title={title}
              icon={icon}
              transitionClasses={transitionClasses}
              isExternal={isExternalLink}
            />

            {children}
          </BaseAction>
        );
      }}
    >
      {title}
    </Tooltip>
  );
};

export default MainNavigationBaseActionLink;
